/**
 * buildFeedbackContext — assembles the four auto-attached diagnostic fields for
 * POST /feedback (IC-001's `context` object), with per-field omission when a
 * value is unavailable.
 *
 * Implements US-008 (T1, AC-008.5). No React in this module.
 *
 * TO-LL-042 enforcement (build id): reads the build identifier via
 * frontend/src/lib/buildId.ts's getBuildId() — the single shared read point
 * TS-003 introduced — rather than reading import.meta.env.VITE_APP_BUILD_ID
 * directly. getBuildId() itself falls back to the literal UNKNOWN_BUILD_ID
 * ('unknown') when the variable is missing, empty or whitespace-only (its own
 * TO-LL-036 fail-safe for errorReporter.ts's consumption). This module treats
 * that literal as "no real build id available" and OMITS the appVersion key
 * entirely in that case, per the PRD's stated fallback and per US-008's own
 * instruction not to send the literal 'unknown' onto the wire — do not weaken
 * this back to always-include-the-string.
 *
 * TO-LL-044 enforcement: the only key this builder ever sets is one of
 * pagePath/appVersion/userAgent/mostRecentSessionId (exact camelCase, matching
 * IC-001's context object).
 *
 * Never put userId, createdAt, senderName or senderEmail here. IC-001's
 * allowlist silently drops all four from the request body: userId resolves
 * server-side from the authenticated identity, createdAt is server-assigned,
 * and the parent's reply-to address is read from their own account record
 * (FR-4.4). Adding any of them here would be dead code that looks security
 * relevant to the next reader.
 */

import { getBuildId, UNKNOWN_BUILD_ID } from '@/lib/buildId';
import type { FeedbackContext } from '@/types/feedback';

const MAX_PAGE_PATH_LENGTH = 200;
const MAX_APP_VERSION_LENGTH = 64;
const MAX_USER_AGENT_LENGTH = 400;
const MAX_SESSION_ID_LENGTH = 64;

/**
 * Build the diagnostic context for a feedback submission.
 *
 * @param mostRecentSessionId - The parent's most recent session id across all
 *   children (derived by the page from the already-loaded useParentSessions
 *   query, DD-010). Included only when a non-empty string is passed.
 * @returns FeedbackContext with every field either present (capped to its
 *   contract length) or omitted (never an empty string, never 'unknown').
 */
export function buildFeedbackContext(mostRecentSessionId?: string): FeedbackContext {
  const context: FeedbackContext = {};

  // pagePath: pathname ONLY — never the query string or hash. The dashboard
  // genuinely carries markers such as ?checkout=success; a diagnostic field
  // must never become a place URL parameters are copied into a stored record.
  try {
    if (typeof window !== 'undefined' && window.location) {
      const pathname = window.location.pathname;
      if (typeof pathname === 'string' && pathname.length > 0) {
        context.pagePath = pathname.slice(0, MAX_PAGE_PATH_LENGTH);
      }
    }
  } catch {
    // window/location unavailable in this context (e.g. some test environments) — omit.
  }

  // appVersion: via the shared getBuildId() read point. Omit entirely when it
  // resolves to the literal 'unknown' sentinel rather than sending that string.
  const buildId = getBuildId();
  if (buildId !== UNKNOWN_BUILD_ID) {
    context.appVersion = buildId.slice(0, MAX_APP_VERSION_LENGTH);
  }

  // userAgent: same cap the server allowlist and errorReporter.ts both apply.
  // Omitted when navigator is unavailable (some test environments).
  if (typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string') {
    context.userAgent = navigator.userAgent.slice(0, MAX_USER_AGENT_LENGTH);
  }

  // mostRecentSessionId: included only when a non-empty string is passed. A
  // parent with no sessions yet simply has no such field.
  if (typeof mostRecentSessionId === 'string' && mostRecentSessionId.trim().length > 0) {
    context.mostRecentSessionId = mostRecentSessionId.slice(0, MAX_SESSION_ID_LENGTH);
  }

  return context;
}
